import { AiBlock } from "@/components/ax/AiBlock";
import { Button } from "@/components/ax/Button";
import { Brief, BriefLoading } from "@/components/brief/Brief";
import { useI18n } from "@/i18n";

const blockerKey = { locked: "locked", kill: "ai.kill", noConsent: "ai.noConsent", quotaOut: "ai.quotaOut" };

export const BriefPanel = ({ w, items }) => {
  const { t } = useI18n();
  const d = w.decision;
  const b = d.brief;
  const composing = w.busy === "compose";
  const ready = b && !b.abstained && !composing;
  const optionsOk = d.options.length >= 2;
  const attachments = d.attachments ?? [];

  return (
    <section className="mt-12" aria-labelledby="ws-brief" data-testid="workspace-brief">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id="ws-brief" className="caption m-0 text-ax-muted">{t("ws.brief")}</h2>
        {b && !composing && <span className="font-mono text-[13px] leading-[18px] text-ax-muted" data-testid="brief-version">v{b.version}</span>}
        {b && !composing && !b.model && <span className="font-mono text-[13px] leading-[18px] text-ax-muted">{t("brief.reviewNote")}</span>}
      </div>

      {w.blocker && (
        <p data-testid="ai-blocker" className="m-0 mb-3 text-sm leading-5 text-ax-muted">{t(blockerKey[w.blocker])}</p>
      )}

      <Button
        disabled={!w.canAi || !optionsOk || !!w.busy}
        busy={composing}
        onClick={w.compose}
        data-testid="compose-brief-button"
        className="mb-6"
      >
        {composing ? t("brief.composing") : t("ws.compose")}
      </Button>
      {!optionsOk && <p className="m-0 -mt-3 mb-6 text-sm leading-5 text-ax-muted">{t("opt.min")}</p>}

      {w.errors.compose && (
        <p role="alert" data-testid="compose-error" className="m-0 mb-4 text-sm leading-5 text-ax-error">{w.errors.compose}</p>
      )}

      {composing && <BriefLoading />}

      {!composing && !b && <p data-testid="brief-empty" className="m-0 text-base leading-6 text-ax-text-2">{t("workspace.briefEmpty")}</p>}

      {!composing && b?.abstained && (
        <AiBlock testId="brief-abstain">
          <p className="m-0 text-base leading-6 text-ax-text">{t("brief.abstain")}</p>
          {b.followUp && <p className="m-0 mt-2 text-base leading-6 text-ax-text">{b.followUp}</p>}
        </AiBlock>
      )}

      {!composing && ready && <Brief brief={b} items={items} options={d.options}
        onTriggerChange={w.setTriggerChecked} triggerDisabled={!!w.busy} triggerError={w.errors.trigger} />}

      {ready && (
        <div className="mt-4 flex flex-wrap gap-x-4">
          {[
            ["challenge_thinking", "ws.challenge", "challenge-thinking-button"],
            ["what_am_i_missing", "ws.missing", "what-am-i-missing-button"],
          ].map(([mode, key, tid]) => (
            <Button
              key={mode}
              variant="ghost"
              disabled={!w.canAi || !!w.busy}
              busy={w.busy === "challenge"}
              onClick={() => w.challenge(mode)}
              data-testid={tid}
              className="px-0"
            >
              {t(key)}
            </Button>
          ))}
        </div>
      )}
      {w.errors.challenge && (
        <p role="alert" data-testid="challenge-error" className="m-0 mt-2 text-sm leading-5 text-ax-error">{w.errors.challenge}</p>
      )}

      {ready && attachments.length > 0 && (
        <ul data-testid="attachments" className="m-0 mt-4 flex list-none flex-col gap-3 p-0">
          {attachments.map((a) => (
            <li key={a.id}>
              <AiBlock title={t(a.mode === "challenge_thinking" ? "ws.challenge" : "ws.missing")} testId={`attachment-${a.id}`}>
                <p className="m-0 text-base leading-6 text-ax-text [overflow-wrap:anywhere]">{a.text}</p>
                {a.mode === "what_am_i_missing" && (
                  <Button
                    disabled={!!w.busy}
                    onClick={() => w.addItem(a.text, "UNKNOWN", "suggested")}
                    data-testid={`attachment-make-item-${a.id}`}
                    className="mt-3"
                  >
                    {t("ws.makeItem")}
                  </Button>
                )}
              </AiBlock>
            </li>
          ))}
        </ul>
      )}

      {w.committed && (
        <div className="mt-4">
          <Button
            variant="ghost"
            disabled={!w.canAi || !!w.busy}
            busy={w.busy === "continuity"}
            onClick={w.continuity}
            data-testid="continuity-button"
            className="px-0"
          >
            {t("ws.continuity")}
          </Button>
          {w.errors.continuity && (
            <p role="alert" className="m-0 text-sm leading-5 text-ax-error">{w.errors.continuity}</p>
          )}
          {d.continuity && (
            <div className="mt-2">
              <AiBlock testId="continuity-result">
                <p className="m-0 font-mono text-[13px] leading-[18px] text-ax-text" data-testid="continuity-verdict">{t(`continuity.${d.continuity.verdict}`)}</p>
                <p className="m-0 mt-1 text-base leading-6 text-ax-text">{d.continuity.line}</p>
              </AiBlock>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
